import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Alert,
  CircularProgress,
  Grid,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import { useSnackbar } from 'notistack';
import axiosInstance from '../config/axios';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { useNavigate } from 'react-router-dom';

const SolicitudRolMedico = () => {
  const [formData, setFormData] = useState({
    mensaje: '',
    especialidad: '',
    numeroRegistroMedico: '',
    experiencia: '',
    institucionEducativa: '',
    anioGraduacion: ''
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();

  // Verificar si el usuario ya tiene una solicitud pendiente
  useEffect(() => {
    const verificarSolicitudExistente = async () => {
      try {
        const usuarioStorage = JSON.parse(localStorage.getItem('usuario') || '{}');
        if (!usuarioStorage._id) {
          navigate('/login');
          return;
        }

        const response = await axiosInstance.get(`/usuarios/${usuarioStorage._id}`);
        const usuario = response.data;

        if (usuario.rol !== 'paciente') {
          enqueueSnackbar('Solo los pacientes pueden solicitar cambio a rol médico', { 
            variant: 'error' 
          });
          navigate('/perfil');
          return;
        }

        if (usuario.solicitudCambioRol?.estado === 'pendiente') {
          enqueueSnackbar('Ya tienes una solicitud de cambio de rol pendiente', { 
            variant: 'warning' 
          });
          navigate('/perfil');
          return;
        }

        setLoading(false);
      } catch (error) {
        console.error('Error al verificar estado de solicitud:', error);
        enqueueSnackbar('Error al verificar el estado de tu solicitud', { 
          variant: 'error' 
        });
        navigate('/perfil');
      }
    };

    verificarSolicitudExistente();
  }, [navigate, enqueueSnackbar]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    try {
      const response = await axiosInstance.post('/usuarios/solicitar-rol-medico', formData);
      
      setSuccess(true);
      enqueueSnackbar('Solicitud enviada exitosamente', { 
        variant: 'success',
        autoHideDuration: 3000
      });
      
      // Redirigir al perfil después de 2 segundos
      setTimeout(() => {
        navigate('/perfil');
      }, 2000);
    } catch (error) {
      setError(error.response?.data?.message || 'Error al enviar la solicitud');
      enqueueSnackbar(error.response?.data?.message || 'Error al enviar la solicitud', {
        variant: 'error',
        autoHideDuration: 4000
      });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  if (success) {
    return (
      <Alert severity="success" sx={{ mt: 2 }}>
        Tu solicitud ha sido enviada exitosamente. Te notificaremos cuando sea procesada.
        Serás redirigido a tu perfil en unos segundos...
      </Alert>
    );
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', p: 3 }}>
      <Paper elevation={3} sx={{ p: 3 }}>
        <Typography variant="h5" gutterBottom>
          Solicitud para Rol de Médico
        </Typography>
        
        <Typography variant="body2" color="text.secondary" paragraph>
          Complete el siguiente formulario para solicitar convertirse en médico. 
          Un administrador revisará su solicitud y verificará la información proporcionada.
        </Typography>

        <Box sx={{ my: 3 }}>
          <Typography variant="subtitle1" gutterBottom>
            Requisitos:
          </Typography>
          <List>
            <ListItem>
              <ListItemIcon>
                <CheckCircleOutlineIcon color="primary" />
              </ListItemIcon>
              <ListItemText primary="Título médico válido" />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckCircleOutlineIcon color="primary" />
              </ListItemIcon>
              <ListItemText primary="Registro médico activo" />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckCircleOutlineIcon color="primary" />
              </ListItemIcon>
              <ListItemText primary="Documentación que acredite su experiencia" />
            </ListItem>
          </List>
        </Box>

        <Divider sx={{ my: 3 }} />

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Especialidad Médica"
                name="especialidad"
                value={formData.especialidad}
                onChange={handleChange}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Número de Registro Médico"
                name="numeroRegistroMedico"
                value={formData.numeroRegistroMedico}
                onChange={handleChange}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Institución Educativa"
                name="institucionEducativa"
                value={formData.institucionEducativa}
                onChange={handleChange}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Año de Graduación"
                name="anioGraduacion"
                type="number"
                value={formData.anioGraduacion}
                onChange={handleChange}
                required
                inputProps={{ min: "1950", max: new Date().getFullYear() }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Años de Experiencia y Áreas de Especialización"
                name="experiencia"
                multiline
                rows={3}
                value={formData.experiencia}
                onChange={handleChange}
                required
                placeholder="Describa su experiencia profesional, incluyendo años de práctica y áreas de especialización..."
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Mensaje Adicional"
                name="mensaje"
                multiline
                rows={4}
                value={formData.mensaje}
                onChange={handleChange}
                placeholder="Proporcione información adicional que considere relevante para su solicitud..."
              />
            </Grid>
          </Grid>

          <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button
              variant="outlined"
              onClick={() => navigate('/perfil')}
              disabled={loading}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={loading}
            >
              {loading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                'Enviar Solicitud'
              )}
            </Button>
          </Box>
        </form>
      </Paper>
    </Box>
  );
};

export default SolicitudRolMedico; 